import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { Paperclip } from 'lucide-react';
import {
  canDeleteDeliverable,
  canProject,
  type Deliverable,
  type DeliverableKind,
} from '@allemnii-pulse/shared';
import { useCollection } from '../../lib/firestore.js';
import { useProject } from '../projects/ProjectProvider.js';
import { useAuth } from '../auth/AuthProvider.js';
import { api } from '../../lib/api.js';
import { Card } from '../../components/ui/Card.js';
import { Button } from '../../components/ui/Button.js';
import { Input } from '../../components/ui/Input.js';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '../../components/ui/Select.js';
import { Badge } from '../../components/ui/Badge.js';
import { Term } from '../../components/ui/Term.js';

const KIND_LABEL: Record<DeliverableKind, string> = {
  client_folder: 'Client folder',
  screenshot: 'Screenshot',
  deployment: 'Live deployment',
  cloud_project: 'Cloud project',
  link: 'Other links',
  file: 'File',
  slides: 'Slides',
};

/** What a person can pick when adding or editing a deliverable — 'slides' are created and kept
 * up to date by the training pipeline only. */
const SELECTABLE_KINDS = Object.entries(KIND_LABEL).filter(([k]) => k !== 'slides');

function AddDeliverableForm({ onAdded }: { onAdded: () => void }) {
  const { projectId } = useProject();
  const [kind, setKind] = useState<DeliverableKind>('link');
  const [label, setLabel] = useState('');
  const [url, setUrl] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const picked = e.target.files?.[0] ?? null;
    setFile(picked);
    if (picked) setKind(picked.type.startsWith('image/') ? 'screenshot' : 'file');
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!label.trim()) return;
    setBusy(true);
    setError(null);
    try {
      let storagePath: string | undefined;
      let mimeType: string | undefined;
      if (file) {
        const { uploadUrl, storagePath: path } = await api.post<{
          uploadUrl: string;
          storagePath: string;
        }>(`/projects/${projectId}/deliverables/upload-url`, {
          filename: file.name,
          mime: file.type,
        });
        await fetch(uploadUrl, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': file.type },
        });
        storagePath = path;
        mimeType = file.type;
      }
      await api.post(`/projects/${projectId}/deliverables`, {
        kind,
        label: label.trim(),
        url: url.trim() || undefined,
        storagePath,
        mimeType,
      });
      setLabel('');
      setUrl('');
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      onAdded();
    } catch {
      setError(file ? 'Could not upload this file.' : 'Could not add this deliverable.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="p-4">
      {/* Fields stack full-width on a phone; from sm they share one wrapping row. */}
      <form onSubmit={handleSubmit} className="flex flex-wrap items-end gap-2">
        <Select value={kind} onValueChange={(v) => setKind(v as DeliverableKind)}>
          <SelectTrigger className="h-9 w-full text-sm sm:w-auto">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SELECTABLE_KINDS.map(([k, l]) => (
              <SelectItem key={k} value={k}>
                {l}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Label"
          className="basis-full sm:flex-1"
        />
        <Input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="URL (optional)"
          className="basis-full sm:flex-1"
        />
        <label>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,.pdf,.zip,.txt,.md,.doc,.docx"
            className="hidden"
            onChange={handleFileChange}
          />
          <Button type="button" disabled={busy} onClick={() => fileInputRef.current?.click()}>
            <Paperclip className="mr-1.5 h-3.5 w-3.5" />
            <span className="max-w-[8rem] truncate">{file ? file.name : 'Attach file'}</span>
          </Button>
        </label>
        <Button type="submit" variant="default" disabled={busy || !label.trim()}>
          Add
        </Button>
      </form>
      {error && <p className="mt-2 text-sm text-danger">{error}</p>}
    </Card>
  );
}

/** Fetches a short-lived signed download URL and renders an image thumbnail for deliverables
 * with an image mime type. Fetched per-row rather than eagerly for the whole list, since most
 * deliverables (links, deployments) never need one. */
function DeliverableThumbnail({
  projectId,
  deliverable,
}: {
  projectId: string;
  deliverable: Deliverable;
}) {
  const [src, setSrc] = useState<string | null>(null);
  const isImage = Boolean(deliverable.storagePath && deliverable.mimeType?.startsWith('image/'));

  useEffect(() => {
    if (!isImage) return;
    let cancelled = false;
    api
      .get<{ downloadUrl: string }>(
        `/projects/${projectId}/deliverables/${deliverable.id}/download-url`,
      )
      .then((res) => {
        if (!cancelled) setSrc(res.downloadUrl);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [projectId, deliverable.id, isImage]);

  if (!isImage || !src) return null;
  return (
    <img
      src={src}
      alt={deliverable.label}
      className="h-10 w-10 flex-shrink-0 rounded-md border border-border object-cover"
    />
  );
}

function EditDeliverableForm({
  projectId,
  deliverable,
  onDone,
}: {
  projectId: string;
  deliverable: Deliverable;
  onDone: () => void;
}) {
  const [kind, setKind] = useState<DeliverableKind>(deliverable.kind);
  const [label, setLabel] = useState(deliverable.label);
  const [url, setUrl] = useState(deliverable.url ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!label.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await api.patch(`/projects/${projectId}/deliverables/${deliverable.id}`, {
        kind,
        label: label.trim(),
        // The API can't clear a field, so an emptied URL is left unchanged.
        ...(url.trim() ? { url: url.trim() } : {}),
      });
      onDone();
    } catch {
      setError('Could not save changes.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
      <Select value={kind} onValueChange={(v) => setKind(v as DeliverableKind)}>
        <SelectTrigger className="h-9 w-full text-sm sm:w-auto">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {SELECTABLE_KINDS.map(([k, l]) => (
            <SelectItem key={k} value={k}>
              {l}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Input
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        placeholder="Label"
        className="basis-full sm:flex-1"
      />
      <Input
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        placeholder="URL (optional)"
        className="basis-full sm:flex-1"
      />
      <Button type="submit" variant="default" disabled={busy || !label.trim()}>
        Save
      </Button>
      <Button type="button" disabled={busy} onClick={onDone}>
        Cancel
      </Button>
      {error && <p className="w-full text-sm text-danger">{error}</p>}
    </form>
  );
}

async function openDeliverableFile(projectId: string, id: string) {
  const { downloadUrl } = await api.get<{ downloadUrl: string }>(
    `/projects/${projectId}/deliverables/${id}/download-url`,
  );
  window.open(downloadUrl, '_blank', 'noopener,noreferrer');
}

export function DeliverablesPage() {
  const { projectId, role } = useProject();
  const { user } = useAuth();
  const [editingId, setEditingId] = useState<string | null>(null);
  const { data: deliverables, loading } = useCollection<Deliverable>(
    `projects/${projectId}/deliverables`,
  );

  async function handleDelete(id: string) {
    await api.delete(`/projects/${projectId}/deliverables/${id}`);
  }

  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-4 p-4 sm:p-6">
      <div>
        <h1 className="text-lg font-semibold">
          <Term id="deliverable">Deliverables</Term>
        </h1>
        <p className="text-sm text-muted">
          Screenshots, deployment links, and other tangible outputs.
        </p>
      </div>

      {canProject(role, 'deliverable.write') && <AddDeliverableForm onAdded={() => {}} />}

      {loading ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : deliverables.length === 0 ? (
        <Card className="p-6 text-center text-sm text-muted sm:p-8">Nothing added yet.</Card>
      ) : (
        <Card className="flex flex-col divide-y divide-border">
          <AnimatePresence initial={false}>
            {deliverables.map((d) => (
              <motion.div
                key={d.id}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.18 }}
                className="flex items-center gap-3 p-3"
              >
                {editingId === d.id ? (
                  <EditDeliverableForm
                    projectId={projectId}
                    deliverable={d}
                    onDone={() => setEditingId(null)}
                  />
                ) : (
                  <>
                <Badge
                  variant="secondary"
                  className="flex-shrink-0 px-2 py-0.5 text-[10px] uppercase tracking-wide text-muted"
                >
                  {KIND_LABEL[d.kind]}
                </Badge>
                <DeliverableThumbnail projectId={projectId} deliverable={d} />
                {d.kind === 'slides' && d.trainingSessionId ? (
                  <Link
                    to={`/projects/${projectId}/sessions/${d.trainingSessionId}`}
                    className="min-w-0 flex-1 break-words text-sm hover:text-accent"
                  >
                    {d.label}
                  </Link>
                ) : d.url ? (
                  <a
                    href={d.url}
                    target="_blank"
                    rel="noreferrer"
                    className="min-w-0 flex-1 break-words text-sm hover:text-accent"
                  >
                    {d.label}
                  </a>
                ) : d.storagePath ? (
                  <button
                    type="button"
                    onClick={() => void openDeliverableFile(projectId, d.id)}
                    className="min-w-0 flex-1 break-words text-left text-sm hover:text-accent"
                  >
                    {d.label}
                  </button>
                ) : (
                  <span className="min-w-0 flex-1 break-words text-sm">{d.label}</span>
                )}
                {/* -my-3 py-3 grows the touch target to ~40px without changing the row height. */}
                {canProject(role, 'deliverable.write') && d.kind !== 'slides' && (
                  <button
                    onClick={() => setEditingId(d.id)}
                    className="-my-3 py-3 text-xs text-muted hover:text-accent"
                  >
                    Edit
                  </button>
                )}
                {canDeleteDeliverable(role, user?.uid ?? '', d) && (
                  <button
                    onClick={() => void handleDelete(d.id)}
                    className="-my-3 py-3 text-xs text-muted hover:text-danger"
                  >
                    Remove
                  </button>
                )}
                  </>
                )}
              </motion.div>
            ))}
          </AnimatePresence>
        </Card>
      )}
    </div>
  );
}
